import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Loader2, BarChart3, Calendar, ChevronRight } from 'lucide-react';
import { getMarketResearchSessions, getMarketResearchSession } from '../api/api';
import MarketResearchModal from './MarketResearchModal';
import { useToast } from '../hooks/use-toast';

const MarketResearchDashboard = ({ open, onClose, brandId, brandVisualTheme }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && brandId) {
      loadSessions();
    }
  }, [open, brandId]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data = await getMarketResearchSessions(brandId);
      setSessions(data);
    } catch (error) {
      console.error("Failed to load sessions", error);
      toast({ title: "Error", description: "Failed to load research history.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = async (session) => {
    setDetailLoading(true);
    try {
       const fullSession = await getMarketResearchSession(session.id);
       setSelectedSession(fullSession);
    } catch (error) {
       toast({ title: "Error", description: "Failed to load session details.", variant: "destructive" });
    } finally {
       setDetailLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(undefined, { 
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' 
    });
  };

  // ... Styles from visual theme ...
  const palette = brandVisualTheme?.palette || {};
  const primaryColor = palette.primary || '#4f46e5';

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0 gap-0 overflow-hidden">
          <div className="p-6 border-b">
             <DialogTitle className="flex items-center gap-2 text-xl">
                <BarChart3 className="w-6 h-6" style={{ color: primaryColor }} />
                Market Research History
             </DialogTitle>
             <DialogDescription className="mt-1">
               Review past synthetic audience tests and insights.
             </DialogDescription>
          </div>

          <ScrollArea className="flex-1 bg-slate-50/50">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : sessions.length === 0 ? (
               <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                 <BarChart3 className="w-12 h-12 mb-2 opacity-20" />
                 <p>No research sessions found.</p>
               </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {sessions.map(session => (
                  <div 
                    key={session.id} 
                    className="p-4 hover:bg-white hover:shadow-sm transition-all cursor-pointer group"
                    onClick={() => handleSessionClick(session)}
                  >
                    <div className="flex justify-between items-start">
                       <div className="space-y-1">
                          <div className="font-medium text-slate-900 line-clamp-1 pr-4">
                            {session.content.slice(0, 100) || "No content preview"}...
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                             <Badge variant="outline" className="text-xs font-normal bg-slate-50">
                               {session.segmentLabel}
                             </Badge>
                             <span className="text-xs flex items-center gap-1">
                               <Calendar className="w-3 h-3" />
                               {formatDate(session.createdAt)}
                             </span>
                             <span className="text-xs">
                               • {session.identitiesCount} personas
                             </span>
                          </div>
                       </div>
                       
                       <div className="flex items-center gap-4">
                          {session.averageScore !== null && (
                             <div className="text-right">
                                <div className="text-2xl font-bold" style={{ color: primaryColor }}>
                                  {session.averageScore}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Avg Score</div>
                             </div>
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-400" />
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      {selectedSession && (
        <MarketResearchModal
          open={!!selectedSession}
          onClose={() => setSelectedSession(null)}
          brandVisualTheme={brandVisualTheme}
          initialData={selectedSession}
        />
      )}
      
      {/* Global Loading Overlay for Detail Fetch */}
      {detailLoading && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
            <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
               <Loader2 className="w-5 h-5 animate-spin" style={{ color: primaryColor }} />
               <span className="font-medium text-sm">Loading session details...</span>
            </div>
         </div>
      )}
    </>
  );
};

export default MarketResearchDashboard;





